import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import EcfBadge, { type BadgeVariant } from './EcfBadge';
import {
  ecfService,
  type ApprovalPreview,
  type ApprovalResult,
  type EcfConfig,
} from '../../../../services/ecf.service';

/**
 * Panel del Paso 3 de la certificación: las aprobaciones comerciales.
 *
 * Aquí la empresa actúa de receptora: la DGII entrega un set de comprobantes emitidos por otro
 * RNC y hay que devolver por cada uno un ACECF firmado, aceptando o rechazando la transacción.
 * Qué se aprueba y qué se rechaza lo decide el set, no la empresa.
 *
 * Mismo flujo en dos tiempos que el Paso 2 —firmar sin enviar y luego enviar—, pero la DGII
 * responde en el acto: no hay trackId ni estados que refrescar después.
 */

const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  pendiente: { label: 'Pendiente', variant: 'muted' },
  listo: { label: 'Firmada', variant: 'info' },
  aceptado: { label: 'Aceptada', variant: 'success' },
  rechazado: { label: 'Rechazada', variant: 'danger' },
  error: { label: 'Error', variant: 'danger' },
};

interface AprobacionesPanelProps {
  config: EcfConfig;
}

export default function AprobacionesPanel({ config }: AprobacionesPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const [preview, setPreview] = useState<ApprovalPreview | null>(null);
  const [results, setResults] = useState<ApprovalResult[]>([]);

  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setResults(await ecfService.approvalResults());
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'No se pudo cargar el estado',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ambienteOk = config.environment === 'cert';
  const listoParaEnviar = ambienteOk && !!config.emisor_rnc;

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setMessage(null);
    try {
      const p = await ecfService.approvalPreview(file);
      setPreview(p);
      const conError = p.cases.filter((c) => c.status === 'error').length;
      if (conError) {
        setMessage({
          type: 'error',
          text: `${conError} de ${p.total} aprobaciones no se pudieron armar. Revisa el detalle antes de enviar.`,
        });
      } else {
        setMessage({
          type: 'success',
          text: `${p.total} aprobaciones armadas y firmadas: ${p.aprobaciones} de aceptación y ${p.rechazos} de rechazo. Nada se ha enviado todavía.`,
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'No se pudo procesar el set',
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleRun = async () => {
    if (!file || !preview) return;

    const porEnviar = preview.total - preview.yaEnviadas;
    const aviso =
      `Se enviarán ${porEnviar} aprobaciones comerciales a la DGII (ambiente ${preview.environment}).\n\n` +
      `Las que ya fueron aceptadas se saltan automáticamente.\n\n¿Continuar?`;
    if (!confirm(aviso)) return;

    setSending(true);
    setMessage(null);
    try {
      const r = await ecfService.approvalRun(file);
      await load();
      const partes = [`${r.enviadas} aceptadas`];
      if (r.saltadas) partes.push(`${r.saltadas} ya estaban aceptadas`);
      if (r.fallidas) partes.push(`${r.fallidas} con error o rechazo`);
      setMessage({ type: r.fallidas ? 'error' : 'success', text: partes.join(' · ') });
      toast[r.fallidas ? 'warning' : 'success'](`Envío terminado: ${partes.join(', ')}`);
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Falló el envío';
      setMessage({ type: 'error', text });
      toast.error(text);
    } finally {
      setSending(false);
    }
  };

  const handleReset = async () => {
    if (
      !confirm(
        'Esto borra el registro local de las aprobaciones enviadas, pero NO lo deshace en la DGII.\n\n' +
          'Úsalo solo si la DGII reinició tu postulación.\n\n¿Continuar?'
      )
    )
      return;
    try {
      await ecfService.approvalReset();
      setPreview(null);
      await load();
      toast.success('Registro local reiniciado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo reiniciar');
    }
  };

  const descargar = async (encf: string) => {
    try {
      await ecfService.approvalDownloadXml(encf);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo descargar el XML');
    }
  };

  // Los resultados mandan sobre la vista previa: reflejan lo que la DGII respondió de verdad.
  const filas: ApprovalResult[] = results.length ? results : preview?.cases ?? [];
  const aceptadas = results.filter((r) => r.status === 'aceptado').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Requisitos */}
      {!listoParaEnviar && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm space-y-1">
          <p className="font-medium">
            <i className="ri-error-warning-line mr-1"></i>
            Falta configuración antes de poder enviar
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-xs">
            {!ambienteOk && (
              <li>
                El ambiente activo es <strong>{config.environment}</strong>. Las aprobaciones del
                set solo cuentan en CerteCF.
              </li>
            )}
            {!config.emisor_rnc && <li>Falta el RNC de la empresa.</li>}
          </ul>
        </div>
      )}

      {message && (
        <div
          className={`p-4 rounded-lg border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          <i
            className={`${
              message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            } mr-2`}
          ></i>
          {message.text}
        </div>
      )}

      {/* 1. Cargar el set */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">1. Carga el set de aprobaciones</p>
        <p className="text-xs text-gray-500">
          Descárgalo del portal de la DGII con "Descargar aprobaciones comerciales". Cada fila es un
          comprobante que recibiste y que hay que responder con un ACECF firmado; el propio set dice
          cuáles se aprueban y cuáles se rechazan.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
          }}
          className="text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50 file:text-sm file:text-gray-700 file:cursor-pointer hover:file:bg-gray-100"
        />
      </div>

      {/* 2. Firmar sin enviar */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">2. Comprueba antes de enviar</p>
        <p className="text-xs text-gray-500">
          Arma y firma las aprobaciones sin enviarlas, para ver que el set se procesa entero.
        </p>
        <button
          onClick={handlePreview}
          disabled={!file || previewing}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i
            className={`${previewing ? 'ri-loader-4-line animate-spin' : 'ri-quill-pen-line'} mr-2`}
          ></i>
          {previewing ? 'Firmando...' : 'Firmar sin enviar'}
        </button>

        {preview && (
          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-0.5">
            <p>
              Comprador: RNC <strong>{preview.compradorRnc}</strong> · ambiente{' '}
              {preview.environment}
            </p>
            <p>
              {preview.total} aprobaciones: {preview.aprobaciones} de aceptación y{' '}
              {preview.rechazos} de rechazo
              {preview.yaEnviadas > 0 && ` · ${preview.yaEnviadas} ya aceptadas (se saltarán)`}
            </p>
          </div>
        )}

        {/* El set se descarga desde la sesión del portal, así que un RNC ajeno casi siempre
            significa que el archivo cargado es de otra empresa. */}
        {preview && preview.rncAjenos.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-xs">
            <i className="ri-error-warning-line mr-1"></i>
            El set trae aprobaciones dirigidas a {preview.rncAjenos.join(', ')} y tu RNC es{' '}
            {preview.compradorRnc}. Comprueba que descargaste el archivo desde tu propia postulación
            antes de enviarlo.
          </div>
        )}
      </div>

      {/* 3. Enviar */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">3. Envía a la DGII</p>
        <p className="text-xs text-gray-500">
          La DGII responde en el acto: cada aprobación queda aceptada o rechazada sin espera. Las ya
          aceptadas no se reenvían; las rechazadas se pueden corregir y volver a enviar.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleRun}
            disabled={!file || !preview || sending || !listoParaEnviar}
            title={!preview ? 'Primero firma sin enviar' : undefined}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i
              className={`${sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} mr-2`}
            ></i>
            {sending ? 'Enviando a la DGII...' : 'Enviar aprobaciones'}
          </button>

          {results.length > 0 && (
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-red-600 text-sm font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              Reiniciar registro
            </button>
          )}
        </div>
      </div>

      {/* Resultados */}
      {filas.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium text-gray-900">
              Aprobaciones
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({filas.length} en total)
              </span>
            </p>
            {results.length > 0 && (
              <p className="text-xs text-gray-600">
                Aceptadas por la DGII:{' '}
                <strong>
                  {aceptadas}/{filas.length}
                </strong>
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    e-NCF
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RNC emisor
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Respuesta
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    XML
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filas.map((r) => {
                  const badge = STATUS_BADGE[r.status] ?? STATUS_BADGE.pendiente;
                  return (
                    <tr key={r.encf} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono font-semibold text-gray-900">
                        {r.encf}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-gray-600">
                        {r.rnc_emisor ?? '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                        {r.monto
                          ? Number(r.monto).toLocaleString('es-DO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                        {r.estado_set === '2' ? 'Rechazo' : 'Aceptación'}
                      </td>
                      <td className="px-4 py-2">
                        <EcfBadge variant={badge.variant}>{badge.label}</EcfBadge>
                        {r.message && (
                          <span className="block text-xs text-gray-500 mt-1 max-w-md">
                            {r.message}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {r.has_xml && results.length > 0 ? (
                          <button
                            onClick={() => descargar(r.encf)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Descargar ACECF firmado"
                          >
                            <i className="ri-download-line"></i>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
